<script setup lang="ts">
import { computed, type CSSProperties } from 'vue';
import { Loader2, Check } from 'lucide-vue-next';
import { cn } from '@/lib/utils';

type Variant = 'primary' | 'secondary' | 'inverted' | 'ghost';
type Size = 'sm' | 'md' | 'lg' | 'buyButton';
type State = 'idle' | 'pending' | 'success' | 'error' | 'disabled';

const props = withDefaults(
  defineProps<{
    variant?: Variant;
    size?: Size;
    state?: State;
    type?: 'button' | 'submit' | 'reset';
    successColor?: string;
  }>(),
  {
    variant: 'primary',
    size: 'md',
    state: 'idle',
    type: 'button',
  },
);

const variantClasses: Record<Variant, string> = {
  primary: 'bg-primary text-primary-foreground hover:opacity-90',
  secondary: 'bg-secondary text-secondary-foreground hover:opacity-90',
  inverted:
    'bg-background text-foreground border border-foreground/20 hover:bg-foreground hover:text-background',
  ghost: 'bg-transparent hover:bg-foreground/5',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-10 px-4',
  lg: 'h-12 px-6 text-lg',
  buyButton: 'h-11 px-8 text-sm'
};

const stateClasses = computed(() => (props.state === 'error' ? 'animate-shake' : ''));

const inlineStyle = computed<CSSProperties>(() => {
  if (props.state === 'success' && props.successColor) {
    return { backgroundColor: props.successColor };
  }
  return {};
});

const isDisabled = computed(() => props.state === 'pending' || props.state === 'disabled');
</script>

<template>
  <button
    :type="type"
    :disabled="isDisabled"
    :style="inlineStyle"
    :class="
      cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        variantClasses[variant],
        sizeClasses[size],
        stateClasses,
      )
    "
  >
    <Loader2 v-if="state === 'pending'" class="h-4 w-4 animate-spin" />
    <Check v-else-if="state === 'success'" class="h-4 w-4" />
    <slot />
  </button>
</template>
